import { firstDefined } from '@avantia/client-and-server-utilities';
import {
  HpConditionalQuestionText,
  HpControlPrivacyLevelTypes,
  HpControlWidth,
  HpKeyboardTypes,
  createControlId
} from '@avantia/questionset-model';
import { CSSProperties, ElementType } from 'react';
import { FormControl, InputGroup } from 'react-bootstrap';
import { HpControlValueTypes } from '../../../types';
import { HpEventDetail, HpOnFocusEvent, HpOnFormEvent } from '../../../types/inputControlTypes';
import { createFocusEvent, createFormEvent } from '../inputControl';

export interface HpBasicTextInputControlProps {
  className?: string;
  disabled: boolean | undefined;
  eventDetail: HpEventDetail;
  hasCharacterCount?: boolean;
  id: string;
  isMultiLine?: boolean;
  keyboard?: HpKeyboardTypes;
  lineCount?: number;
  maxLength?: number;
  minLength?: number;
  name: string | undefined;
  onBlur: HpOnFocusEvent;
  onChange: HpOnFormEvent;
  onFocus: HpOnFocusEvent;
  placeholder: string | undefined;
  prefixText?: string;
  privacyLevel?: HpControlPrivacyLevelTypes;
  questionText: string | HpConditionalQuestionText[];
  style?: CSSProperties;
  suffixText?: string;
  type: string | undefined;
  value: HpControlValueTypes;
  width: HpControlWidth | undefined;
}

export type HpEventHandlerRegistrationForBasicTextInputControlFunction = (
  controlId: string,
  handlers: HpControlEventHandlers
) => void;

let externalHandler: HpEventHandlerRegistrationForBasicTextInputControlFunction | undefined;

export function registerCustomEventHandlerForBasicTextInputControl(
  handler: HpEventHandlerRegistrationForBasicTextInputControlFunction
): void {
  externalHandler = handler;
}

export function BasicTextInputControl({
  className,
  disabled,
  eventDetail,
  hasCharacterCount,
  id,
  isMultiLine,
  keyboard,
  lineCount,
  maxLength,
  minLength,
  name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  prefixText,
  privacyLevel,
  questionText,
  style,
  suffixText,
  type,
  value
}: HpBasicTextInputControlProps): JSX.Element {
  let textControlType: string | undefined;
  let elementType: ElementType<any> | undefined;
  let characterCountElement: JSX.Element | undefined;
  if (isMultiLine) {
    elementType = 'textarea';
    if (hasCharacterCount && minLength) {
      characterCountElement = createCharacterCountSection((value as string).length, minLength);
    }
  } else {
    textControlType = type || 'text';
  }

  const changeEvent = createFormEvent(onChange, eventDetail);
  const blurEvent = createFocusEvent(onBlur, eventDetail, changeEvent);
  const focusEvent = createFocusEvent(onFocus, eventDetail, changeEvent);
  const controlId = createControlId({ questionId: id });

  // This is a very horrible hack to get events for the email control to work in saveQuoteAction.tsx (Quote Journey).
  // That feature should hopefully be replaced with an automatics save feature in about 3 months if all things go well.
  if (externalHandler) {
    externalHandler(controlId, { changeEvent, blurEvent, focusEvent });
  }

  let textControl = (
    <FormControl
      as={elementType}
      className={`${className || ''} animated`.trimStart()}
      disabled={disabled}
      id={controlId}
      inputMode={keyboard}
      maxLength={maxLength}
      name={name || id}
      onBlur={blurEvent}
      onChange={changeEvent}
      onFocus={focusEvent}
      placeholder={firstDefined<string>(placeholder, typeof questionText === 'string' ? questionText : '')}
      rows={lineCount}
      style={style}
      tabIndex={0}
      type={textControlType}
      value={value as string}
      data-hp-privacy-level={privacyLevel}
    />
  );

  if (prefixText || suffixText || characterCountElement) {
    textControl = (
      <>
        <InputGroup>
          {prefixText && <InputGroup.Text>{prefixText}</InputGroup.Text>}
          {textControl}
          {suffixText && <InputGroup.Text>{suffixText}</InputGroup.Text>}
        </InputGroup>
        {characterCountElement}
      </>
    );
  }

  return textControl;
}

function createCharacterCountSection(inputCount: number, minLength: number): JSX.Element {
  const cssClass = inputCount >= minLength ? 'hp-success-indicator' : 'av-error-message';
  return (
    <>
      <div className="hp-character-count">
        <span className={cssClass}>{inputCount}</span>
      </div>
    </>
  );
}

export interface HpControlEventHandlers {
  blurEvent: (event: React.FocusEvent<HTMLElement, Element>) => void;
  changeEvent: React.ChangeEventHandler<HTMLElement>;
  focusEvent: (event: React.FocusEvent<HTMLElement, Element>) => void;
}
